import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '@/api/client'
import { useEffectiveAccess } from '@/api/queries'
import { Button } from '@/components/ui/Button'

interface ReloadStatus {
  hotReloadEnabled: boolean
  bridge: {
    status: string
    loadedSha: string | null
    loadedCount: number | null
    asOf: string | null
    lastReloadOk: boolean | null
    lastReloadFile: string | null
    lastReloadError: string | null
  } | null
}

export function ReloadControl() {
  const qc = useQueryClient()
  const me = useEffectiveAccess()
  const status = useQuery({
    queryKey: ['reload', 'status'],
    queryFn: async () => (await apiFetch<ReloadStatus>('/api/reload/status')).data,
    refetchInterval: 10_000,
  })
  const reloadNow = useMutation({
    mutationFn: async () => (await apiFetch<{ status: string }>('/api/reload/now', { method: 'POST' })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reload', 'status'] }),
  })
  const toggle = useMutation({
    mutationFn: async (enabled: boolean) => (await apiFetch<{ hotReloadEnabled: boolean }>('/api/reload/toggle', {
      method: 'PUT', body: JSON.stringify({ enabled }),
    })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reload', 'status'] }),
  })
  const canManage = (me.data?.capabilities ?? []).includes('access.manage')

  return (
    <div className="page">
      <div className="page-head">
        <div><h1>Reload control</h1><p>Manual bridge reload + hot-reload toggle.</p></div>
        <Button variant="primary" disabled={!canManage || reloadNow.isPending} onClick={() => reloadNow.mutate()}
          title={!canManage ? 'Administrator only' : ''} busy={reloadNow.isPending}>Reload now</Button>
      </div>

      <div className="stack-16">
        <div className="card pad">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 'var(--fs-400)' }}>
                Hot-reload {status.data?.hotReloadEnabled ? 'enabled' : 'disabled'}
              </div>
              <p className="muted" style={{ fontSize: 'var(--fs-200)' }}>
                {status.data?.hotReloadEnabled
                  ? 'Bridge watches the share and reloads automatically when a file changes.'
                  : 'Automatic reloading is off — changes wait for a manual reload.'}
              </p>
            </div>
            <Button disabled={!canManage} onClick={() => toggle.mutate(!status.data?.hotReloadEnabled)}>
              {status.data?.hotReloadEnabled ? 'Disable' : 'Enable'} hot-reload
            </Button>
          </div>
        </div>

        <div className="card pad">
          <h3 style={{ marginTop: 0 }}>Bridge /health</h3>
          {status.data?.bridge ? (
            <table className="tbl">
              <tbody>
                <tr><th style={{ textAlign: 'left' }}>status</th><td className="mono">{status.data.bridge.status}</td></tr>
                <tr><th style={{ textAlign: 'left' }}>personas.loaded.count</th><td className="mono">{status.data.bridge.loadedCount}</td></tr>
                <tr><th style={{ textAlign: 'left' }}>personas.loaded.sha</th><td className="mono">{status.data.bridge.loadedSha}</td></tr>
                <tr><th style={{ textAlign: 'left' }}>asOf</th><td className="mono">{status.data.bridge.asOf}</td></tr>
                <tr><th style={{ textAlign: 'left' }}>lastReload.ok</th><td className="mono">{String(status.data.bridge.lastReloadOk)}</td></tr>
                <tr><th style={{ textAlign: 'left' }}>lastReload.file</th><td className="mono">{status.data.bridge.lastReloadFile ?? '—'}</td></tr>
                {status.data.bridge.lastReloadError && (
                  <tr><th style={{ textAlign: 'left', color: 'var(--err)' }}>error</th><td style={{ color: 'var(--err)' }}>{status.data.bridge.lastReloadError}</td></tr>
                )}
              </tbody>
            </table>
          ) : <p className="muted">Bridge /health unreachable.</p>}
        </div>
      </div>
    </div>
  )
}
